// src/pages/ProductDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Stack,
  Tabs,
  Tab,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Inventory,
  Timeline,
  Analytics,
  Info,
  Refresh,
  Edit,
  Print,
  QrCode,
  Assignment,
  SwapHoriz,
  ArrowBack,
  FilterAlt,
  FilterAltOff,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import StatusCard from 'src/components/StatusCard';
import Loader from 'src/components/Loader';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import { DataService } from 'src/services/data-service';

// Types
import {
  ProductData,
  InventorySummary,
  ProductStats,
  InventoryLocation,
  Movement,
  SimilarProduct,
  ApiResponse,
  ProductDetailsResponse,
  InventoryResponse,
  MovementResponse,
  SimilarProductsResponse,
  TransferHistoryItem,
  TransferHistoryResponse,
  TransferSummary
} from 'src/types/Product-details.types';
import UnitDialog from './ProductDialog';
import { EnhancedTableColumn, EnhancedTableColumnType } from 'src/components/enhanced-table';
import { useAppSelector } from 'src/redux/hooks';
import { ROLE } from 'src/services/permission-service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ProductDetailPage: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<ProductData | null>(null);
  const [inventorySummary, setInventorySummary] = useState<InventorySummary | null>(null);
  const [stats, setStats] = useState<ProductStats | null>(null);
  const [inventoryLocations, setInventoryLocations] = useState<InventoryLocation[]>([]);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transferHistory, setTransferHistory] = useState<TransferHistoryItem[]>([]);
  const [transferSummary, setTransferSummary] = useState<TransferSummary | null>(null);
  const [movementPagination, setMovementPagination] = useState<any>(null);
  const [similarProducts, setSimilarProducts] = useState<SimilarProduct[]>([]);

  const userRole = useAppSelector((s) => s.user.user.role) as ROLE;
  const userId = useAppSelector((s) => s.user.user.id);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const [tabValue, setTabValue] = useState(0);

  // Show zeros toggle for Inventory API
  const [showZeros, setShowZeros] = useState(false);
  const isClientContext = userRole === ROLE.CLIENT && !!userId;

  // Inventory table columns
  const inventoryColumns: EnhancedTableColumn[] = [
    {
      id: 'warehouseName',
      label: 'Warehouse',
      minWidth: 150,
      type: EnhancedTableColumnType.CUSTOM,
      render: (value: string, row: InventoryLocation) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/warehouse/${row.warehouseId}`)}>
          <Typography color="primary" sx={{ fontWeight: 'medium' }}>
            {value}
          </Typography>
        </Box>
      )
    },
    {
      id: 'storageUnitName',
      label: 'Storage Unit',
      minWidth: 150,
      type: EnhancedTableColumnType.CUSTOM,
      render: (value: string, row: InventoryLocation) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/storage-unit/${row.storageUnitId}`)}>
          <Typography color="primary" sx={{ fontWeight: 'medium' }}>
            {value}
          </Typography>
        </Box>
      )
    },
    { id: 'storageUnitCode', label: 'Unit Code', minWidth: 120, type: EnhancedTableColumnType.TEXT },
    {
      id: 'boxRefNumber',
      label: 'Box Ref',
      minWidth: 120,
      type: EnhancedTableColumnType.CLICKABLE,
      onClick: (value, row) => {
        navigate(`/box/${row.boxRefNumber}`);
      },
      render: (value: string) => (
        <Typography variant="body2" color={value ? 'primary' : 'text.secondary'}>
          {value || '—'}
        </Typography>
      )
    },
    {
      id: 'quantity',
      label: 'Quantity',
      minWidth: 100,
      type: EnhancedTableColumnType.NUMBER,
      render: (value: number) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: value > 0 ? 'success.main' : 'text.secondary'
          }}
        >
          {value}
        </Typography>
      )
    },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      type: EnhancedTableColumnType.DATE,
      minWidth: 150,
      render: (value: string) => new Date(value).toLocaleDateString()
    }
  ];

  const transferColumns: EnhancedTableColumn[] = [
    {
      id: 'timestamp',
      label: 'Date & Time',
      minWidth: 150,
      type: EnhancedTableColumnType.DATE,
      render: (value: string) => (
        <Typography variant="body2">
          {new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Typography>
      )
    },
    {
      id: 'operationType',
      label: 'Type',
      type: EnhancedTableColumnType.CUSTOM,
      minWidth: 120,
      render: (value: number, row: TransferHistoryItem) => {
        const effectiveType = isClientContext && value === 2 ? 1 : value;
        const getTypeLabel = (type: number) => {
          switch (type) {
            case 1:
              return { label: 'Move In', color: 'success' };
            case 2:
              return { label: 'Move Out', color: 'warning' };
            case 3:
              return { label: 'Reallocation', color: 'secondary' };
            case 4:
              return { label: 'Shipment', color: 'info' };
            case 5:
              return { label: 'Transfer', color: 'primary' };
            default:
              return { label: 'Unknown', color: 'default' };
          }
        };
        const typeInfo = getTypeLabel(effectiveType);
        return (
          <Chip
            label={typeInfo.label}
            size="small"
            color={typeInfo.color as any}
            variant="outlined"
          />
        );
      }
    },
    // Direction chip: compact and smaller font
    {
      id: 'direction',
      label: 'Direction',
      minWidth: 90,
      type: EnhancedTableColumnType.CUSTOM,
      render: (_: any, row: TransferHistoryItem) => {
        const isIn = row.direction === 1; // 1 = IN, 2 = OUT
        return (
          <Chip
            icon={isIn ? <ArrowDownward fontSize="inherit" /> : <ArrowUpward fontSize="inherit" />}
            label={isIn ? 'IN' : 'OUT'}
            size="small"
            color={isIn ? 'success' : 'error'}
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              '& .MuiChip-icon': { fontSize: 14 }
            }}
          />
        );
      }
    },
    // Qty always has +/− sign and color
    {
      id: 'quantity',
      label: 'Qty',
      minWidth: 90,
      type: EnhancedTableColumnType.NUMBER,
      render: (value: number, row: TransferHistoryItem) => {
        const isIn = row.direction === 1;
        const sign = isIn ? '+' : '−';
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: isIn ? 'success.main' : 'error.main'
            }}
          >
            {sign}
            {Math.abs(value ?? 0)}
          </Typography>
        );
      }
    },
    {
      id: 'boxRefNumber',
      label: 'Container',
      minWidth: 140,
      type: EnhancedTableColumnType.CLICKABLE,
      render: (value: string) => (
        <Typography variant="body2" color={value ? 'primary' : 'text.secondary'}>
          {value || 'Independent'}
        </Typography>
      ),
      onClick: (value, row) => {
        if (value) navigate(`/box/${row.boxRefNumber}`);
      }
    },
    {
      id: 'unitDisplay',
      label: 'Unit',
      minWidth: 180,
      type: EnhancedTableColumnType.CUSTOM,
      render: (_: any, row: TransferHistoryItem) => {
        const isIn = row.direction === 1;
        const name = isIn ? row.destinationName : row.sourceName;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }} color="primary">
            {name || '—'}
          </Typography>
        );
      }
    }
  ];

  useEffect(() => {
    if (!productId) {
      navigate('/Units');
      return;
    }
  }, [productId, navigate]);

  const loadProductDetails = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await DataService.get(`api/Unit/${productId}`);
      const result: ApiResponse<ProductData> = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to load product details');
      }

      if (result.data) {
        setProduct(result.data);
      } else {
        throw new Error('Product not found');
      }
    } catch (err) {
      console.error('Error loading product details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryLocations = async () => {
    if (!productId) return;

    try {
      setInventoryLoading(true);

      const baseUrl = `api/InventoryItem/product/${productId}/current-inventory`;
      const params: string[] = [];
      if (isClientContext) params.push(`clientId=${encodeURIComponent(userId!)}`);
      if (showZeros) params.push('showZero=true');

      const apiUrl = params.length ? `${baseUrl}?${params.join('&')}` : baseUrl;

      const response = await DataService.get(apiUrl);
      const result: ApiResponse<InventoryResponse> = await response.json();

      if (result.success && result.data) {
        setInventoryLocations(result.data.locations);
        if (result.data.summary) {
          setInventorySummary(result.data.summary);
        }
      }
    } catch (err) {
      console.error('Error loading inventory:', err);
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadMovements = async () => {
    if (!productId) return;

    try {
      setMovementsLoading(true);

      const baseUrl = `api/Operation/unit/${productId}/transfers?page=1&pageSize=50`;
      const apiUrl = isClientContext ? `${baseUrl}&clientId=${encodeURIComponent(userId!)}` : baseUrl;

      const response = await DataService.get(apiUrl);

      if (response.ok) {
        const result = await response.json();

        if (result && result.items) {
          const mappedItems = result.items.map((item: any) => {
            const effectiveOperationType = isClientContext && item.operationType === 2 ? 1 : item.operationType;

            return {
              id: item.id,
              unitId: item.unitId,
              itemName: item.itemName || 'Unknown Product',
              itemBarcode: item.unitBarcode || item.barcode,
              quantity: item.quantity,
              direction: item.direction,
              operationType: effectiveOperationType,
              sourceType: item.sourceType,
              sourceId: item.sourceId,
              sourceName: item.sourceName || 'Unknown Source',
              destinationType: item.destinationType,
              destinationId: item.destinationId,
              destinationName: item.destinationName || 'Unknown Destination',
              boxRefNumber: item.boxRefNumber,
              timestamp: item.timestamp,
              operationId: item.operationId
            } as unknown as TransferHistoryItem;
          });

          setTransferHistory(mappedItems);
          setMovementPagination({
            currentPage: result.pageNumber || 1,
            pageSize: result.pageSize || 50,
            totalCount: result.totalCount || result.items?.length || 0,
            totalPages: result.totalPages || Math.ceil((result.items?.length || 0) / 50),
            hasNextPage: result.hasNext || false,
            hasPreviousPage: result.hasPrevious || false
          });
        }
      }
    } catch (err) {
      console.error('Error loading transfer data:', err);
    } finally {
      setMovementsLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (productId) {
      loadProductDetails();
    }
  }, [productId]);

  useEffect(() => {
    if (product && tabValue === 1) {
      loadInventoryLocations();
    }
  }, [product, tabValue, showZeros, isClientContext, userId]);

  useEffect(() => {
    if (product && tabValue === 2) {
      loadMovements();
    }
  }, [product, tabValue, isClientContext, userId]);

  // Debug logging
  useEffect(() => {
    console.log('Inventory locations:', inventoryLocations);
    console.log('Movements:', movements);
  }, [inventoryLocations, movements]);

  const handleTransferSuccess = (result: any) => {
    console.log('Transfer completed from product:', result);
    handleRefreshData();
    setTransferDialogOpen(false);
  };

  const handleRefreshData = () => {
    loadProductDetails();
    if (tabValue === 1) loadInventoryLocations();
    if (tabValue === 2) loadMovements();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePrintBarcode = (size: 'A4' | 'A5') => {
    setPrintDialogOpen(false);

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const pageStyle = size === 'A4'
      ? 'width: 210mm; height: 297mm;'
      : 'width: 148mm; height: 210mm;';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Barcode - ${product?.itemName}</title>
          <style>
            @page {
              size: ${size};
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              ${pageStyle}
            }
            .barcode-container {
              text-align: center;
              padding: 20px;
              border: 2px solid #333;
              border-radius: 8px;
            }
            .product-name {
              font-size: ${size === 'A4' ? '24px' : '18px'};
              font-weight: bold;
              margin-bottom: 20px;
            }
            .barcode {
              font-size: ${size === 'A4' ? '48px' : '36px'};
              font-family: 'Courier New', monospace;
              letter-spacing: 4px;
              margin: 20px 0;
            }
            .product-info {
              font-size: ${size === 'A4' ? '16px' : '14px'};
              color: #666;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <div class="product-name">${product?.itemName || product?.name}</div>
            <div class="barcode">${product?.barcode}</div>
            <div class="product-info">Symbol: ${product?.symbol}</div>
            <div class="product-info">Size: ${size}</div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 2 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Loader
              type="warehouse"
              size="large"
              message="Loading Product Details..."
              subMessage="Fetching inventory and movement data"
              showProgress={true}
            />
          </Box>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 2 }}>
        <Container maxWidth="xl">
          <StatusCard
            variant="error"
            title="Error Loading Product"
            message={error}
            actionLabel="Try Again"
            onAction={loadProductDetails}
            layout="page"
          />
        </Container>
      </Box>
    );
  }

  // Product not found
  if (!product) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 2 }}>
        <Container maxWidth="xl">
          <StatusCard
            variant="not-found"
            title="Product Not Found"
            message="The requested product could not be found. Please check the product ID and try again."
            actionLabel="Go Back"
            onAction={() => navigate('/Units')}
            layout="page"
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Compact Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{
                      bgcolor: 'grey.100',
                      '&:hover': { bgcolor: 'grey.200' }
                    }}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>

                  <Typography variant="h5" fontWeight="600">
                    {product.itemName || product.name}
                  </Typography>
                  <Chip label={product.isSynced ? 'Synced' : 'Not Synced'} color={product.isSynced ? 'success' : 'warning'} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                  {product.barcode}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={product.symbol} size="small" variant="outlined" />
                  {(product.itemAdditionalProperties?.Brand || product.additionalProperties?.Brand) && (
                    <Chip
                      label={product.itemAdditionalProperties?.Brand || product.additionalProperties?.Brand}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <IconButton size="small" onClick={handleRefreshData} title="Refresh">
                  <Refresh />
                </IconButton>
                <Button size="small" startIcon={<Print />} variant="outlined" onClick={() => setPrintDialogOpen(true)}>
                  Print
                </Button>
                <Button size="small" startIcon={<Edit />} variant="contained" onClick={() => setEditDialogOpen(true)}>
                  Edit
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Quick Stats */}
          {inventorySummary && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="600" color="primary">
                      {inventorySummary.totalQuantity}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Stock
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="600" color="info.main">
                      {inventorySummary.warehouseCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Warehouses
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="600" color="warning.main">
                      {inventorySummary.storageUnitCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Storage Units
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="600" color="success.main">
                      {transferSummary?.totalShipmentReceives || transferHistory.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transfers
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Product Details" icon={<Info />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontWeight: 500 }} />
            <Tab
              label={`Inventory (${inventoryLocations.length})`}
              icon={<Inventory />}
              iconPosition="start"
              sx={{ minHeight: 48, textTransform: 'none', fontWeight: 500 }}
            />
            <Tab
              label={`Movements (${transferHistory.length})`}
              icon={<Timeline />}
              iconPosition="start"
              sx={{ minHeight: 48, textTransform: 'none', fontWeight: 500 }}
            />
            {/* <Tab label="Analytics" icon={<Analytics />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontWeight: 500 }} /> */}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Stacked Layout */}
          <Stack spacing={1}>
            {/* Basic Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Barcode
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      mt: 0.5
                    }}
                  >
                    {product.barcode}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Symbol
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {product.symbol}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Unit of Measurement
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {product.unitOfMeasurement || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Properties Count
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                    {(() => {
                      const properties = product.itemAdditionalProperties || product.additionalProperties;
                      return properties ? Object.keys(properties).length : 0;
                    })()}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    On Hand Quantity
                  </Typography>
                  <Typography variant="h6" color="success.main" sx={{ mt: 0.5 }}>
                    {product.onHandQuantity ?? 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Expected Quantity
                  </Typography>
                  <Typography variant="h6" color="info.main" sx={{ mt: 0.5 }}>
                    {product.expectedQuantity ?? 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Min Number
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {product.itemMinNumber ?? 'Not Set'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Max Number
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {product.itemMaxNumber ?? 'Not Set'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Additional Properties */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Additional Properties
              </Typography>
              {(() => {
                const properties = product.itemAdditionalProperties || product.additionalProperties;
                return properties && Object.keys(properties).length > 0 ? (
                  <Grid container spacing={2}>
                    {Object.entries(properties).map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Typography variant="caption" color="text.secondary">
                          {key}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {value as string}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No additional properties defined
                  </Typography>
                );
              })()}
            </Paper>

            {/* System Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                System Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {new Date(product.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {new Date(product.updatedAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Synced Status
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {product.isSynced ? 'Yes' : 'No'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Unit ID
                  </Typography>
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    sx={{
                      display: 'block',
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      mt: 0.5,
                      wordBreak: 'break-all'
                    }}
                  >
                    {product.id}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Item ID
                  </Typography>
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    sx={{
                      display: 'block',
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      mt: 0.5,
                      wordBreak: 'break-all'
                    }}
                  >
                    {product.itemId || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <EnhancedTable
            title="Inventory Locations"
            subtitle={`Stock available in ${inventoryLocations.length} locations`}
            columns={inventoryColumns}
            data={inventoryLocations.reduce((acc, location, index) => {
              acc[`${location.warehouseId}-${location.storageUnitId}-${index}`] = location;
              return acc;
            }, {} as Record<string, InventoryLocation>)}
            loading={inventoryLoading}
            emptyMessage="No inventory found for this product"
            wmsMode={true}
            densityLevel="compact"
            selection={{ showCheckbox: false }}
            actions={[
              {
                key: 'toggle-zeros',
                title: showZeros ? 'Hide Zeros' : 'Show Zeros',
                icon: showZeros ? <FilterAltOff /> : <FilterAlt />,
                onClick: () => setShowZeros((v) => !v)
              },
              {
                key: 'refresh',
                title: 'Refresh',
                icon: <Refresh />,
                onClick: () => loadInventoryLocations()
              }
            ]}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <EnhancedTable
            title="Transfer History"
            subtitle={`${transferHistory.length} total transfers`}
            columns={transferColumns}
            data={transferHistory.reduce((acc, transfer, index) => {
              acc[transfer.id || index.toString()] = transfer;
              return acc;
            }, {} as Record<string, TransferHistoryItem>)}
            loading={movementsLoading}
            emptyMessage="No transfer history found for this product"
            wmsMode={true}
            densityLevel="compact"
            selection={{ showCheckbox: false }}
            actions={[
              {
                key: 'refresh',
                title: 'Refresh',
                icon: <Refresh />,
                onClick: () => loadMovements()
              }
            ]}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Analytics Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Product analytics and reporting features will be available here.
            </Typography>
          </Box>
        </TabPanel>
      </Card>

      <UnitDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        unit={product ?? null}
        mode="edit"
        onSuccess={(updatedUnit) => {
          setProduct(updatedUnit);
          handleRefreshData();
        }}
      />

      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Print Size</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the paper size for printing the barcode label
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => handlePrintBarcode('A4')}
              sx={{ py: 2 }}
            >
              <Stack spacing={0.5}>
                <Typography variant="button">A4</Typography>
                <Typography variant="caption" color="text.secondary">
                  210 x 297 mm
                </Typography>
              </Stack>
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => handlePrintBarcode('A5')}
              sx={{ py: 2 }}
            >
              <Stack spacing={0.5}>
                <Typography variant="button">A5</Typography>
                <Typography variant="caption" color="text.secondary">
                  148 x 210 mm
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductDetailPage;
